import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackOrder } from '../services/orderApi';
import API_BASE_URL from '../config/api';
import { getTranslatedProduct } from '../utils/translationUtils';
import { useCurrency } from '../context/CurrencyContext';

const TrackYourOrder = () => {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);

  const orderNumber = new URLSearchParams(location.search).get('order') || '';

  useEffect(() => {
    if (!orderNumber) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await trackOrder(orderNumber);
        console.log('Track order response:', response);
        // Handle different response formats
        if (response?.success && response?.data) {
          setOrderData(response.data);

          // Check if this is a guest order from this device and clear cart if paid
          const guestOrders = JSON.parse(localStorage.getItem('guest_orders') || '[]');
          if (
            guestOrders.includes(orderNumber) &&
            ['paid', 'pending'].includes(response.data.paymentStatus) &&
            localStorage.getItem('cart')
          ) {
            console.log('Clearing cart for successful guest order');
            localStorage.removeItem('cart');
            // Dispatch event to update cart count in header if needed
            window.dispatchEvent(new Event('storage'));
          }
        } else if (response?.orderNumber) {
          setOrderData(response);
        } else {
          setError('Order not found');
        }
      } catch (e) {
        console.error('Track order error:', e);
        setError(e.message || 'Failed to track order');
      } finally {
        setLoading(false);
      }
    })();
  }, [orderNumber]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="mt-[78px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-[900px] mx-auto py-10">
        <h1 className="text-[28px] sm:text-[36px] font-semibold mb-6">
          {t('trackOrder.title') || 'Track Your Order'}
        </h1>

        {!orderNumber && (
          <div className="py-16 text-center text-gray-600">
            <div className="text-6xl mb-4">📦</div>
            <p>
              {t('trackOrder.enterOrderNumber') ||
                'Please provide an order number in the URL'}
            </p>
            <p className="text-sm mt-2">
              {t('trackOrder.example') || 'Example: ?order=ORD-XXXX'}
            </p>
          </div>
        )}

        {orderNumber && loading && (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p>{t('trackOrder.loading') || 'Loading order details...'}</p>
          </div>
        )}

        {orderNumber && !loading && error && (
          <div className="py-16 text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-red-600 text-lg">
              {t('trackOrder.error') || error}
            </div>
            <p className="text-gray-500 mt-2">
              {t('trackOrder.checkNumber') ||
                'Please check your order number and try again'}
            </p>
          </div>
        )}

        {orderNumber && !loading && !error && orderData && (
          <div className="space-y-8">
            {/* Order Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-500">
                    {t('trackOrder.orderNumber') || 'Order Number'}
                  </div>
                  <div className="text-lg font-semibold">
                    {orderData.orderNumber}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">
                    {t('trackOrder.orderDate') || 'Order Date'}
                  </div>
                  <div className="text-lg">
                    {formatDate(orderData.createdAt)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">
                    {t('trackOrder.status') || 'Status'}
                  </div>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(orderData.status)}`}
                  >
                    {t(`checkout.status.${orderData.status}`) ||
                      orderData.status?.charAt(0).toUpperCase() +
                      orderData.status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4">
                {t('trackOrder.summary') || 'Order Summary'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t('trackOrder.paymentStatus') || 'Payment Status'}:
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${getPaymentStatusColor(orderData.paymentStatus)}`}
                    >
                      {t(`checkout.status.${orderData.paymentStatus}`) ||
                        (orderData.paymentStatus === 'paid'
                          ? 'Paid'
                          : orderData.paymentStatus === 'failed'
                            ? 'Failed'
                            : 'Pending')}
                    </span>
                  </div>
                  {orderData.payment?.method && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('trackOrder.paymentMethod') || 'Payment Method'}:
                      </span>
                      <span className="font-medium">
                        {orderData.payment.method
                          .replace('_', ' ')
                          .toUpperCase()}
                      </span>
                    </div>
                  )}
                  {orderData.payment?.last4 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('orders.card') || 'Card'}:
                      </span>
                      <span className="font-medium">
                        **** {orderData.payment.last4} (
                        {orderData.payment.brand})
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t('trackOrder.subtotal') || 'Subtotal'}:
                    </span>
                    <span className="font-medium">
                      {formatPrice(orderData.subtotal || 0)}
                    </span>
                  </div>
                  {orderData.shippingCost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('trackOrder.shipping') || 'Shipping'}:
                      </span>
                      <span className="font-medium">
                        {formatPrice(orderData.shippingCost)}
                      </span>
                    </div>
                  )}
                  {orderData.tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('trackOrder.tax') || 'Tax'}:
                      </span>
                      <span className="font-medium">
                        {formatPrice(orderData.tax)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-lg font-semibold">
                      {t('trackOrder.total') || 'Total'}:
                    </span>
                    <span className="text-lg font-semibold">
                      {formatPrice(orderData.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items */}
            {orderData.items && orderData.items.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h2 className="text-xl font-semibold mb-4">
                  {t('trackOrder.items') || 'Order Items'} (
                  {orderData.items.length})
                </h2>
                <div className="space-y-4">
                  {orderData.items.map((item, index) => (
                    <div
                      key={item._id || index}
                      className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg"
                    >
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <img
                          src={(() => {
                            const img = item.image || item.product?.images?.[0];
                            if (!img) return '/assets/product-placeholder.webp';
                            if (
                              img.startsWith('http') ||
                              img.startsWith('/assets')
                            )
                              return img;
                            // Remove /api from base URL and append image path
                            const baseUrl = API_BASE_URL.replace(
                              /\/api\/?$/,
                              ''
                            );
                            return `${baseUrl}${img.startsWith('/') ? '' : '/'}${img}`;
                          })()}
                          alt={
                            item.product?.name || item.productName || 'Product'
                          }
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/product-placeholder.webp';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">
                          {getTranslatedProduct(
                            item.product,
                            'name',
                            i18n.language
                          ) ||
                            item.productName ||
                            'Unknown Product'}
                        </h3>
                        <div className="text-sm text-gray-600 mt-1">
                          <span>
                            {t('cart.quantity') || 'Qty'}: {item.quantity}
                          </span>
                          {item.color && (
                            <span className="ml-3">
                              {t('product.color') || 'Color'}: {item.color}
                            </span>
                          )}
                          {item.size && (
                            <span className="ml-3">
                              {t('product.size') || 'Size'}: {item.size}
                            </span>
                          )}
                        </div>
                        {item.productSku && (
                          <div className="text-xs text-gray-500 mt-1">
                            SKU: {item.productSku}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          {formatPrice(
                            item.totalPrice ||
                            item.unitPrice * item.quantity ||
                            0
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatPrice(item.unitPrice || 0)}{' '}
                          {t('cart.each') || 'each'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipping Information */}
            {orderData.shippingAddress && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h2 className="text-xl font-semibold mb-4">
                  {t('trackOrder.shippingInfo') || 'Shipping Information'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-2">
                      {t('checkout.shippingAddress') || 'Shipping Address'}
                    </h3>
                    <div className="text-gray-600 space-y-1">
                      <div>
                        {orderData.shippingAddress.firstName}{' '}
                        {orderData.shippingAddress.lastName}
                      </div>
                      <div>{orderData.shippingAddress.address1}</div>
                      {orderData.shippingAddress.address2 && (
                        <div>{orderData.shippingAddress.address2}</div>
                      )}
                      <div>
                        {orderData.shippingAddress.city},{' '}
                        {orderData.shippingAddress.state}{' '}
                        {orderData.shippingAddress.zipCode}
                      </div>
                      <div>{orderData.shippingAddress.country}</div>
                      {orderData.shippingAddress.phone && (
                        <div>
                          {t('checkout.phone') || 'Phone'}:{' '}
                          {orderData.shippingAddress.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">
                      {t('trackOrder.shippingDetails') || 'Shipping Details'}
                    </h3>
                    <div className="space-y-2">
                      {orderData.shippingStatus && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {t('trackOrder.shippingStatus') ||
                              'Shipping Status'}
                            :
                          </span>
                          <span className="font-medium capitalize">
                            {t(`checkout.status.${orderData.shippingStatus}`) ||
                              orderData.shippingStatus.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                      {orderData.shipping?.method && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {t('trackOrder.method') || 'Method'}:
                          </span>
                          <span className="font-medium capitalize">
                            {orderData.shipping.method}
                          </span>
                        </div>
                      )}
                      {orderData.shipping?.trackingNumber && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {t('trackOrder.tracking') || 'Tracking'}:
                          </span>
                          <span className="font-mono text-blue-600">
                            {orderData.shipping.trackingNumber}
                          </span>
                        </div>
                      )}
                      {orderData.shipping?.estimatedDelivery && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {t('trackOrder.estDelivery') || 'Est. Delivery'}:
                          </span>
                          <span className="font-medium">
                            {formatDate(orderData.shipping.estimatedDelivery)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Timeline */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4">
                {t('trackOrder.timeline') || 'Order Timeline'}
              </h2>
              <div className="space-y-4">
                {orderData.createdAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('checkout.orderPlaced') || 'Order Placed'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.createdAt)}
                      </div>
                    </div>
                  </div>
                )}
                {orderData.payment?.processedAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('checkout.paymentCompleted') || 'Payment Processed'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.payment.processedAt)}
                      </div>
                    </div>
                  </div>
                )}
                {orderData.processingAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('checkout.processingOrder') || 'Order Processing'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.processingAt)}
                      </div>
                    </div>
                  </div>
                )}
                {orderData.shippedAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-purple-500 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('trackOrder.shipped') || 'Order Shipped'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.shippedAt)}
                      </div>
                    </div>
                  </div>
                )}
                {orderData.deliveredAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-600 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('trackOrder.delivered') || 'Order Delivered'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.deliveredAt)}
                      </div>
                    </div>
                  </div>
                )}
                {orderData.cancelledAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500 mt-1"></div>
                    <div>
                      <div className="font-medium">
                        {t('checkout.paymentCancelled') || 'Order Cancelled'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(orderData.cancelledAt)}
                      </div>
                      {orderData.cancellationReason && (
                        <div className="text-sm text-gray-500">
                          {t('trackOrder.reason') || 'Reason'}:{' '}
                          {orderData.cancellationReason}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackYourOrder;
